import {
  APIAttachment,
  APIFieldType,
  APIOrganizationUser,
  ArdoqBlob,
  Described,
  Named,
  Operator,
  ResourceType,
  SortOrder,
  WithArdoqPersistent,
} from './apiTypes';
import { ArdoqId, Entity, MetaData, Persisted, Versioned } from './coreTypes';
import { APIScopeDataCore } from './scopeDataAPITypes';
import { QueryBuilderQuery } from './advancedSearchApiTypes';

export enum Aggregate {
  SUM = 'sum',
  COUNT = 'count',
  // Currently not implemented
  // COUNT_NOT_SET = 'count-not-set',
  // COUNT_SET = 'count-set',
  MAX = 'max',
  MIN = 'min',
  AVERAGE = 'avg',
  AVERAGE_SET = 'avg-set',
  MEDIAN = 'median',
}

export enum ReportColumnType {
  FIELD = 'field',
  GREMLIN_PATH = 'gremlin-path',
  REFERENCE_TYPE_INCOMING = 'reference-type-incoming',
  REFERENCE_TYPE_OUTGOING = 'reference-type-outgoing',
  CUSTOM = 'custom', // For general structure gremlin queries where we don't really know what the column represents
}

export type ReportColumn = {
  key: string;
  label: string;
  originalLabel?: string; // only renamed column has this value
  type: ReportColumnType | null;
  dataType?: APIFieldType | null;
  sort?: SortOrder | null;
  isDateRangeColumn?: boolean;
  numberFormatOptions?: string | null;
};

export type APIReportError = {
  firstSeen: string;
  errorKey: any;
  display?: string;
};

export enum DataSourceType {
  ADVANCED_SEARCH = 'advancedSearchPostgres',
  GREMLIN_SEARCH = 'graphSearch',
}

export type SelectedAggregate = Aggregate | null;
export type GremlinQuery = string;
export type ReportQuery = GremlinQuery | QueryBuilderQuery;

export type NonPersistedReport = {
  name: string;
  description: string | null;
  datasource: DataSourceType;
  surveyId?: ArdoqId | null;
  workspaceIds: ArdoqId[] | null; // Can be null on reports created from migration
  columns: ReportColumn[];
  query: ReportQuery | null;
  selectedAggregate: SelectedAggregate;
  aiPrompt?: string | null;
};

export type Report = Persisted<
  NonPersistedReport & {
    queryId: ArdoqId;
    createdDuringMigration?: boolean;
    hasHistoricalData?: boolean;
    _version: number;
    error?: APIReportError;
  }
> &
  WithArdoqPersistent;

export type UpdateReport = NonPersistedReport & {
  queryId: ArdoqId;
  _id: ArdoqId;
  _version: number;
};

export type AIDescriptionResponse = {
  text: string;
  type: 'description' | 'excuse';
};

export type APIReportAttributes = Entity &
  MetaData &
  ArdoqBlob &
  Named &
  Described &
  Versioned &
  Report;

export enum EnhancedSearchStrategy {
  ADVANCED_COMPONENT = 'advanced-component',
  ADVANCED_REFERENCE = 'advanced-reference',
  GREMLIN_REGULAR = 'gremlin-regular',
  GREMLIN_PATH = 'gremlin-path',
  GENERAL_STRUCTURE = 'general-structure',
  SIMPLE_LIST_STRUCTURE = 'simple-list-structure',
  SINGLE_VALUE_STRUCTURE = 'single-value-structure',
  NO_STRATEGY_FOUND = 'no-strategy-found',
  NO_STORED_QUERY_FOUND_NO_STRATEGY_FOUND = 'no-stored-query-found-no-strategy-found',
}

export type UserDataForRenderers = Pick<
  APIOrganizationUser,
  'name' | 'email' | '_id' | 'image'
>;

export type DrilldownScopeData = APIScopeDataCore & {
  attachments: Array<APIAttachment>;
  users: Array<UserDataForRenderers>;
};

export type ReportSearchAggregatesArgs = {
  query: GremlinQuery | QueryBuilderQuery;
  datasource: DataSourceType;
  // workspaceIds are currently only used for reports.
  // TODO AM to use discriminated union to tighten-up types here
  workspaceIds?: ArdoqId[] | null;
};

export type ReportSearchArgs = ReportSearchAggregatesArgs & {
  from: number;
  size: number;
  sort?: string | null;
  order?: SortOrder;
};

export type APISearchResponse = {
  aggregates?: Record<
    ReportColumn['key'],
    Record<Aggregate, number | null>
  > | null;
  columns: ReportColumn[];
  goneColumns: ReportColumn[];
  datasource: DataSourceType;
  description: string | null;
  error?: string;
  name: string;
  results: Array<Record<ReportColumn['key'], any>>;
  selectedAggregate: SelectedAggregate | null;
  strategy: EnhancedSearchStrategy;
  totalNumberOfRows: number;
  filters?: APIReportFilters;
  scopeData: DrilldownScopeData | null;
  /* Only for inventory */
  workspaceIdsForReportDataSelection?: ArdoqId[];
};

export type APISearchAggregatesResponse = {
  aggregates: Record<
    ReportColumn['key'],
    Record<Aggregate, number | null>
  > | null;
};

export type ReportDatasourceMigrationData = {
  name: string;
  _id: ArdoqId;
  createdBy: ArdoqId | null;
  reason: string;
  query: Report['query'];
  updatedQuery: Report['query'];
  invalidQueries: string[];
};

export type APIReportDatasourceMigrationData = Omit<
  ReportDatasourceMigrationData,
  'invalidQueries' | 'migrateReport'
>;

export type ReportSlideParams = {
  sort?: string | null;
  order?: SortOrder;
  filters: APIReportFilters | null;
};

export type NumberInterval = {
  start: number | null;
  end: number | null;
};

// TODO: split API and state types
export type DateInterval = {
  start: Date | null;
  end: Date | null;
};

export type ISODateInterval = {
  start: string | null;
  end: string | null;
};

export type FilterValue =
  | string
  | boolean
  | number
  | NumberInterval
  | ISODateInterval
  | null;

export type FilterExpression = {
  value: FilterValue;
  operator: Operator;
  label?: string | null;
  displayValue?: FilterValue;
};

export type FilterSuggestionSecondaryInformation = {
  kind: 'workspace' | 'email';
  label: string;
};

export type APIReportFilters = Record<string, { values: FilterExpression[] }>;

export type APIReportColumnSuggestions = Array<{
  label: string;
  value: string | ArdoqId;
  secondaryInformation?: FilterSuggestionSecondaryInformation[];
}>;

export type Aggregates = Record<string, Record<Aggregate, number | null>>;

export type APIAggregatesResponse = {
  aggregates: Aggregates | null;
  totalNumberOfRows: number;
};

export type ApiViewStateResponse = {
  entityType: ResourceType;
  entityId: ArdoqId;
  stateId: ArdoqId;
};

export type ApiReportViewStateResponse = ApiViewStateResponse & {
  entityType: ResourceType.REPORT;
};

export type ReportViewState = ApiReportViewStateResponse & {
  state: {
    filters: APIReportFilters;
  };
};
